// Leaders Service for API communication

export interface Leader {
  id: number;
  name: string;
  designation: string;
  image: string | null;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface LeaderRequest {
  name: string;
  designation: string;
  image?: string;
  sort_order?: number;
  is_visible?: boolean;
}

interface LeadersResponse {
  success: boolean;
  data: Leader[];
  message?: string;
}

interface LeaderResponse {
  success: boolean;
  data: Leader;
  message?: string;
}

interface DeleteResponse {
  success: boolean;
  message: string;
}

class LeadersService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
  }

  /**
   * Get all leaders (admin view)
   */
  async getAll(): Promise<LeadersResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/leaders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching all leaders:', error);
      return {
        success: false,
        data: [],
        message: 'Failed to fetch leaders'
      };
    }
  }

  /**
   * Get visible leaders only (public view)
   */
  async getVisible(): Promise<LeadersResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/leaders/visible`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching visible leaders:', error);
      return {
        success: false,
        data: [],
        message: 'Failed to fetch leaders'
      };
    }
  }

  /**
   * Get leader by ID
   */
  async getById(id: number): Promise<LeaderResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/leaders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching leader by ID:', error);
      return {
        success: false,
        data: {} as Leader,
        message: 'Failed to fetch leader'
      };
    }
  }

  /**
   * Create new leader with file upload
   */
  async create(leaderData: LeaderRequest, imageFile?: File): Promise<LeaderResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const formData = new FormData();
      
      formData.append('name', leaderData.name);
      formData.append('designation', leaderData.designation);
      if (leaderData.sort_order !== undefined) {
        formData.append('sort_order', leaderData.sort_order.toString());
      }
      if (leaderData.is_visible !== undefined) {
        formData.append('is_visible', leaderData.is_visible.toString());
      }
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetch(`${this.baseUrl}/leaders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating leader:', error);
      return {
        success: false,
        data: {} as Leader,
        message: 'Failed to create leader'
      };
    }
  }

  /**
   * Update leader with file upload
   */
  async update(id: number, leaderData: Partial<LeaderRequest>, imageFile?: File): Promise<LeaderResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const formData = new FormData();
      
      if (leaderData.name) formData.append('name', leaderData.name);
      if (leaderData.designation) formData.append('designation', leaderData.designation);
      if (leaderData.sort_order !== undefined) {
        formData.append('sort_order', leaderData.sort_order.toString());
      }
      if (leaderData.is_visible !== undefined) {
        formData.append('is_visible', leaderData.is_visible.toString());
      }
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetch(`${this.baseUrl}/leaders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating leader:', error);
      return {
        success: false,
        data: {} as Leader,
        message: 'Failed to update leader'
      };
    }
  }

  /**
   * Delete leader
   */
  async delete(id: number): Promise<DeleteResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/leaders/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting leader:', error);
      return {
        success: false,
        message: 'Failed to delete leader'
      };
    }
  }

  /**
   * Toggle leader visibility
   */
  async toggleVisibility(id: number): Promise<LeaderResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/leaders/${id}/toggle-visibility`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling leader visibility:', error);
      return {
        success: false,
        data: {} as Leader,
        message: 'Failed to toggle visibility'
      };
    }
  }

  /**
   * Update sort orders
   */
  async updateSortOrders(sortOrders: { id: number; sort_order: number }[]): Promise<DeleteResponse> {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/leaders/sort-order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sortOrders })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating sort orders:', error);
      return {
        success: false,
        message: 'Failed to update sort orders'
      };
    }
  }
}

// Export singleton instance
const leadersService = new LeadersService();
export default leadersService;
